/**
 * I conti di un mese che il gestionale non conosce, inseriti dall'host.
 *
 * Una finestra piccola, quattro importi e le notti, e sotto il conto che ne
 * esce mentre si scrive: chi inserisce un numero deve vedere subito dove va a
 * finire. Il conto dell'anteprima è lo stesso che fa il server
 * (`utileDichiaratoCents`, in `@otto/shared`), e il server rifiuta di salvare
 * se il suo non coincide — quello che si vede è quello che si salva.
 */

import { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import {
  formatEur,
  formatMese,
  meseChiudibile,
  todayInRome,
  utileDichiaratoCents,
  type Mese,
  type ProfittoImmobile,
} from "@otto/shared";
import { ApiError, api } from "../lib/api.ts";
import { messaggioDi } from "../lib/errori.ts";
import { centesimiDa, testoDa } from "../lib/importi.ts";
import { Modal } from "../ui/Modal.tsx";
import { Pill, cx } from "../ui/primitives.tsx";

/** Quanto c'è già, per riempire il modulo quando si corregge un mese dichiarato. */
function valoriDi(p: ProfittoImmobile | null) {
  const per = (categoria: string) =>
    (p?.righe ?? [])
      .filter((r) => r.categoria === categoria)
      .reduce((s, r) => s + r.importoCents, 0);
  const commissioni = per("commissioni_ota");
  const imposta = per("tassa_soggiorno");
  return {
    incassato: testoDa(p?.ricaviLordiCents ?? 0),
    commissioni: testoDa(commissioni),
    imposta: testoDa(imposta),
    altri: testoDa((p?.costiOperativiCents ?? 0) - commissioni - imposta),
    notti:
      p && p.nottiConosciute > 0 && p.nottiVendute > 0
        ? String(p.nottiVendute)
        : "",
  };
}

export function DichiaraConti({
  aperta,
  onChiudi,
  listingId,
  nickname,
  mese,
  profitto,
  onSalvato,
}: {
  aperta: boolean;
  onChiudi: () => void;
  listingId: string;
  nickname: string;
  mese: Mese;
  /** Il conto di adesso: dichiarato (si corregge) o del gestionale (si sostituisce). */
  profitto: ProfittoImmobile | null;
  onSalvato: (profitto: ProfittoImmobile) => void;
}) {
  const dichiarato = Boolean(profitto?.dichiaratoIl);
  const [campi, setCampi] = useState(() =>
    valoriDi(dichiarato ? profitto : null),
  );
  const [sostituisci, setSostituisci] = useState(false);
  // Quello che il gestionale sa già. Lo dice anche il server con un 409 quando
  // la fotografia chiusa sa più del conto di oggi: in quel caso il suo numero
  // arriva nel messaggio, e la casella compare lo stesso.
  const [conflitto, setConflitto] = useState<string | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Ogni volta che si apre, si riparte da quello che c'è adesso.
  useEffect(() => {
    if (!aperta) return;
    setCampi(valoriDi(dichiarato ? profitto : null));
    setSostituisci(false);
    setConflitto(null);
    setErrore(null);
  }, [aperta, dichiarato, profitto]);

  const gestionaleCents =
    !dichiarato && profitto ? profitto.ricaviLordiCents : 0;
  const serveConferma = gestionaleCents > 0 || conflitto !== null;

  const letti = useMemo(
    () => ({
      incassato: centesimiDa(campi.incassato),
      commissioni: centesimiDa(campi.commissioni),
      imposta: centesimiDa(campi.imposta),
      altri: centesimiDa(campi.altri),
    }),
    [campi],
  );
  const giorni = new Date(
    Number(mese.slice(0, 4)),
    Number(mese.slice(5, 7)),
    0,
  ).getDate();
  const notti = campi.notti.trim() === "" ? null : Number(campi.notti);
  const nottiValide =
    notti === null ||
    (Number.isInteger(notti) && notti >= 0 && notti <= giorni);
  const importiValidi = Object.values(letti).every((v) => v !== null);
  const utile = importiValidi
    ? utileDichiaratoCents({
        incassatoCents: letti.incassato!,
        commissioniCents: letti.commissioni!,
        impostaSoggiornoCents: letti.imposta!,
        altriCostiCents: letti.altri!,
      })
    : null;

  const puoSalvare =
    importiValidi &&
    nottiValide &&
    campi.incassato.trim() !== "" &&
    (!serveConferma || sostituisci) &&
    !salvando;

  const salva = async () => {
    if (!puoSalvare) return;
    setSalvando(true);
    setErrore(null);
    try {
      const { profitto: nuovo } = await api.dichiaraConti(listingId, {
        mese,
        incassatoCents: letti.incassato!,
        commissioniCents: letti.commissioni!,
        impostaSoggiornoCents: letti.imposta!,
        altriCostiCents: letti.altri!,
        nottiVendute: notti,
        sostituisci,
      });
      onSalvato(nuovo);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setConflitto(e.message);
      } else {
        setErrore(messaggioDi(e, "Non sono riuscito a salvare: riprova."));
      }
    } finally {
      setSalvando(false);
    }
  };

  const cambia =
    (campo: keyof typeof campi) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setCampi((c) => ({ ...c, [campo]: e.target.value }));

  return (
    <Modal
      open={aperta}
      onClose={onChiudi}
      stretta
      title={dichiarato ? "Correggi i conti" : "I conti di questo mese"}
      subtitle={`${nickname} · ${formatMese(mese)}`}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Pill variant="ghost" size="sm" onClick={onChiudi}>
            Annulla
          </Pill>
          <Pill
            variant="primary"
            size="sm"
            icon={<Check size={15} />}
            onClick={() => void salva()}
            loading={salvando}
            disabled={!puoSalvare}
          >
            Salva
          </Pill>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] leading-relaxed text-ink-soft">
          Questi numeri entrano nei conti, nei totali e nel fascicolo per il
          commercialista.
        </p>

        {/* Su un mese che non è ancora chiuso, inserire i numeri lo chiude:
            le prenotazioni che arrivano dopo su Guesty non entrano più. Si
            dice prima del tap, non dopo. */}
        {!dichiarato && !meseChiudibile(mese, todayInRome()) ? (
          <p className="rounded-card bg-warn-soft px-3.5 py-2.5 text-[13px] leading-relaxed text-ink">
            <span className="capitalize">{formatMese(mese)}</span> non è ancora
            chiuso. Con i tuoi numeri Otto smette di contarlo da Guesty: le
            prenotazioni che arrivano dopo non entrano.
          </p>
        ) : null}

        <Campo
          etichetta="Incassato"
          spiegazione="Quanto ti hanno versato i canali per i check-in del mese. Airbnb è già al netto della sua commissione."
          valore={campi.incassato}
          onChange={cambia("incassato")}
          errato={letti.incassato === null}
          segnaposto="es. 3.200,00"
          obbligatorio
        />
        <Campo
          etichetta="Commissioni pagate a parte"
          spiegazione="Ad esempio la fattura di Booking."
          valore={campi.commissioni}
          onChange={cambia("commissioni")}
          errato={letti.commissioni === null}
        />
        <Campo
          etichetta="Imposta di soggiorno versata"
          spiegazione="Quella versata al Comune."
          valore={campi.imposta}
          onChange={cambia("imposta")}
          errato={letti.imposta === null}
        />
        <Campo
          etichetta="Altri costi"
          spiegazione="Pulizie, biancheria, utenze, manutenzione."
          valore={campi.altri}
          onChange={cambia("altri")}
          errato={letti.altri === null}
        />

        <label className="flex flex-col gap-1">
          <span className="text-[12.5px] font-semibold text-ink-soft">
            Notti vendute{" "}
            <span className="font-normal text-ink-faint">(facoltativo)</span>
          </span>
          <input
            type="text"
            inputMode="numeric"
            value={campi.notti}
            onChange={cambia("notti")}
            placeholder={`da 0 a ${giorni}`}
            className={cx(
              "w-32 rounded-field bg-surface-muted px-3 py-2 text-[14px] tabular-nums text-ink outline-none placeholder:text-ink-faint",
              !nottiValide && "ring-1 ring-danger",
            )}
          />
          {!nottiValide ? (
            <span className="text-[12px] text-danger">
              {formatMese(mese)} ha {giorni} notti.
            </span>
          ) : null}
        </label>

        {/* ── Il conto che ne esce, mentre si scrive ──────────────────── */}
        <div
          className={cx(
            "flex items-center justify-between gap-3 rounded-card px-4 py-3",
            utile !== null && utile < 0
              ? "bg-danger-soft text-danger"
              : "bg-surface-muted text-ink",
          )}
        >
          <div className="min-w-0">
            <p className="text-[13.5px] font-bold">Utile della casa</p>
            <p className="text-[12px] opacity-70">
              incassato meno commissioni, imposta e costi
            </p>
          </div>
          <p className="shrink-0 text-[20px] font-extrabold tabular-nums">
            {utile === null ? "-" : formatEur(utile)}
          </p>
        </div>

        {serveConferma ? (
          <label className="flex items-start gap-2.5 rounded-card bg-warn-soft px-3.5 py-3 text-[13px] leading-relaxed text-ink">
            <input
              type="checkbox"
              checked={sostituisci}
              onChange={(e) => setSostituisci(e.target.checked)}
              className="mt-1 size-4 shrink-0"
            />
            <span>
              {conflitto ??
                `Guesty ha già ${formatEur(gestionaleCents)} di incassato per questo mese.`}{" "}
              <b>Usa i miei numeri al posto di questi.</b>
            </span>
          </label>
        ) : null}

        {errore ? (
          <p className="text-[13px] text-danger" role="alert">
            {errore}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

function Campo({
  etichetta,
  spiegazione,
  valore,
  onChange,
  errato,
  segnaposto = "0",
  obbligatorio,
}: {
  etichetta: string;
  spiegazione: string;
  valore: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  errato: boolean;
  segnaposto?: string;
  obbligatorio?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[12.5px] font-semibold text-ink-soft">
        {etichetta}
        {obbligatorio ? null : (
          <span className="font-normal text-ink-faint"> (facoltativo)</span>
        )}
      </span>
      <span
        className={cx(
          "flex items-center gap-1.5 rounded-field bg-surface-muted px-3",
          errato && "ring-1 ring-danger",
        )}
      >
        <span className="text-sm text-ink-faint">€</span>
        <input
          type="text"
          inputMode="decimal"
          value={valore}
          onChange={onChange}
          placeholder={segnaposto}
          aria-label={etichetta}
          className="w-full min-w-0 bg-transparent py-2 text-[14px] tabular-nums text-ink outline-none placeholder:text-ink-faint"
        />
      </span>
      {errato ? (
        <span className="text-[12px] text-danger">
          Importo non valido. Scrivilo come 1.234,56.
        </span>
      ) : (
        <span className="text-[12px] leading-snug text-ink-faint">
          {spiegazione}
        </span>
      )}
    </label>
  );
}
